import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Radar, ArrowDownWideNarrow } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { ExploreFilters } from '@/components/explore/ExploreFilters';
import { OpportunityCard } from '@/components/cards/Cards';
import { SectionHeader, EmptyState, DemoBadge } from '@/components/common/Primitives';
import { useData } from '@/hooks/useData';
import { VisitorOnly } from '@/hooks/useIsVisitor';
import { useSeo } from '@/lib/seo';
import { filterOpportunities, sortOpportunities, paramsToFilters, filtersToParams } from '@/lib/filters';
import { isRecent } from '@/lib/format';
import { stateName } from '@/data/states';

export default function Opportunities() {
  const [sp, setSp] = useSearchParams();
  const filters = paramsToFilters(sp);
  const sort = filters.sort || 'recentes';
  const showClosed = sp.get('encerradas') === '1';
  const { opportunities, companyById, publicOpportunities } = useData();
  useSeo({ title: filters.uf ? `Oportunidades em ${stateName(filters.uf)}` : 'Radar de oportunidades', description: 'Oportunidades comerciais entre empresas: fornecedores, distribuidores, representantes e parcerias.' });

  const base = useMemo(() => (showClosed ? [...publicOpportunities, ...opportunities.filter((o) => o.status === 'encerrada')] : publicOpportunities), [showClosed, publicOpportunities, opportunities]);
  const list = useMemo(() => sortOpportunities(filterOpportunities(base, filters, companyById), sort, companyById), [base, sp.toString(), companyById]); // eslint-disable-line react-hooks/exhaustive-deps
  const recent = list.filter((o) => isRecent(o.publishedAt));
  const older = list.filter((o) => !isRecent(o.publishedAt));

  const update = (f, extra = {}) => {
    const p = filtersToParams({ ...f, sort: f.sort ?? filters.sort });
    if ((extra.encerradas ?? showClosed)) p.encerradas = '1';
    setSp(p, { replace: true });
  };

  return (
    <div className="container-x py-10 sm:py-14">
      <SectionHeader
        eyebrow="Central de oportunidades"
        title="Radar de oportunidades"
        text="Empresas procurando fornecedores, distribuidores, representantes, parceiros e clientes corporativos."
        right={<VisitorOnly><DemoBadge label="Publicações de exemplo" /></VisitorOnly>}
      />
      <ExploreFilters filters={filters} onChange={(f) => update(f)} onClear={() => update({ sort: filters.sort })} fields={['q', 'uf', 'categoryId', 'type', 'period']} resultCount={list.length} testPrefix="opps-filters" />

      <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2">
          <Switch id="show-closed" checked={showClosed} onCheckedChange={(v) => update(filters, { encerradas: v })} data-testid="opps-show-closed-switch" />
          <Label htmlFor="show-closed" className="text-sm text-muted-foreground">Incluir encerradas</Label>
        </div>
        <div className="flex items-center gap-2">
          <ArrowDownWideNarrow className="h-4 w-4 text-muted-foreground" />
          <Label htmlFor="opps-sort" className="text-sm text-muted-foreground">Ordenar por</Label>
          <Select value={sort} onValueChange={(v) => update({ ...filters, sort: v })}>
            <SelectTrigger id="opps-sort" className="h-9 w-[170px] border-white/10 bg-[#0b0f16]" data-testid="opps-sort-select"><SelectValue /></SelectTrigger>
            <SelectContent className="border-white/10 bg-[#0b0f16]">
              <SelectItem value="recentes">Mais recentes</SelectItem>
              <SelectItem value="relevantes">Mais relevantes</SelectItem>
              <SelectItem value="regiao">Região</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="mt-6" data-testid="opps-list">
        {!list.length && <EmptyState icon={Radar} title="Nenhuma oportunidade encontrada" text="Ajuste os filtros ou publique uma oportunidade para sua empresa." action="Limpar filtros" onAction={() => update({ sort: filters.sort })} testId="opps-empty" />}
        {list.length > 0 && sort === 'recentes' ? (
          <div className="space-y-8">
            {recent.length > 0 && (
              <div>
                <div className="mb-3 flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-[rgb(255,130,150)]"><span className="h-1.5 w-1.5 rounded-full bg-[#ff3b5c] shadow-[0_0_6px_#ff3b5c]" />Publicadas nos últimos 7 dias · {recent.length}</div>
                <div className="grid gap-3" data-testid="opps-recent-group">{recent.map((o) => <OpportunityCard key={o.id} opp={o} />)}</div>
              </div>
            )}
            {older.length > 0 && (
              <div>
                <div className="mb-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">Publicações anteriores · {older.length}</div>
                <div className="grid gap-3" data-testid="opps-older-group">{older.map((o) => <OpportunityCard key={o.id} opp={o} />)}</div>
              </div>
            )}
          </div>
        ) : (
          <div className="grid gap-3">{list.map((o) => <OpportunityCard key={o.id} opp={o} />)}</div>
        )}
      </div>
    </div>
  );
}